/**
 * Create a page for all wpPost nodes automatically via
 * filesystem routing: https://www.gatsbyjs.com/docs/reference/routing/file-system-route-api/
 */
import * as React from "react"
import { graphql } from "gatsby"
import { UserInterfaceContext } from "../context/UserInterfaceContext"
import IWpPost from "../interfaces/IWpPost"
import WordpressContent from "../components/wordpress-content"
import MainLayout from "../layouts/main"
import { IYoastBreadcrumb } from "../components/breadcrumbs"
import CategoriesRow from "../components/categories-row"
import FocusGradientAccelerator from "../components/smallAcceleratorImage"
import Link from "../components/link"
import BlogCategoryIcon from "../icons/blog-category"
import dateToString from "../utils/date-to-string"
import SocialShareIcons from "../components/social-share-icons"
import flatListToHierarchical from "../utils/flat-list-to-hierarchical"
import findLocalMenu from "../utils/find-local-menu-item"
import FullWidthPost from "../views/full-width-post"
import makeProdUrl from "../utils/make-prod-url"

interface IPostProps {
  data: {
    wpPost: Queries.PostContentQuery
  }
  pageContext: {
    id: string
    slugs: Array<string>
    breadcrumbs: Array<IYoastBreadcrumb>
  }
}

export default function Post(props: IPostProps): JSX.Element {
  const post = props.data.wpPost
  const { setCurrentPost } = React.useContext(UserInterfaceContext)

  // TODO: set currentPost with pageContext to see if it prevents null on load
  React.useEffect(() => {
    setCurrentPost(post)
  }, [props.data, setCurrentPost])

  const html =
    post?.content?.replace(
      /href="https:\/\/(www.georgiancollege.ca|www.gcblueprint.local|georgiangatsby.wpengine.com)\//gim,
      `href="/`
    ) || ``

  // local menu
  const menuItems = flatListToHierarchical(props?.data?.allWpMenuItem?.nodes)
  let localMenuItem = null
  if (post.uri?.includes(`/blog/`)) {
    localMenuItem = findLocalMenu(216273, menuItems)
  }

  // console.log(props)

  // get first level from post uri without slashes
  const postUri = post.uri?.split(`/`)
  const postUriFirstLevel = postUri?.[1]

  return (
    <MainLayout
      localMenuItem={localMenuItem}
      breadcrumbs={post.breadcrumbs.replaceAll(`category\\\/blog`, `blog`)}
    >
      {[
        `profile`,
        `faculty-profile`,
        `hbec-mentors`,
        `cari-researchers`,
        `cari-projects`,
        `black-excellence`,
      ].includes(postUriFirstLevel) ? (
        <FullWidthPost {...props} />
      ) : (
        <>
          <div className="lg:flex gap-8">
            <div className="">
              <SocialShareIcons
                icons={[`facebook`, `twitter`, `email`, `linkedin`, `whatsapp`]}
                message={post.title || ``}
                uri={`${post.uri}`}
                stacked
                className="w-10 h-10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
              <div className="col-span-2">
                <h2> {post.title}</h2>
                <h2 aria-hidden="true"> {dateToString(new Date(post?.date), false)}</h2>
                <WordpressContent html={html} />
              </div>
              <aside className="col-span-1">
                <div>
                  <FocusGradientAccelerator />
                </div>

                <div className="bg-lightgray mt-6 px-5 py-5">
                  <h2 className="border-b-0 text-black">Related posts</h2>

                  {post.relatedPosts?.map((rpost, index) => (
                    <li
                      key={index}
                      className="flex gap-4 items-start mb-4 group"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-white flex items-center justify-center group-hover:vivid-gradient bg-blue rounded-full w-8 h-8 font-semibold grow-0 shrink-0">
                          <BlogCategoryIcon
                            className="w-5 h-5 text-white"
                            slug={rpost?.primaryCategorySlug}
                          />
                        </span>
                      </div>
                      <div>
                        <Link
                          to={rpost?.uri || ``}
                          className="text-blue underline font-semibold text-base"
                        >
                          {rpost.title}
                        </Link>
                      </div>
                    </li>
                  ))}
                </div>
              </aside>
            </div>
          </div>
          <CategoriesRow></CategoriesRow>
        </>
      )}
    </MainLayout>
  )
}

// adding page title
export const Head = ({
  data,
}: {
  data: Queries.PostContentQuery
}): JSX.Element => (
  <>
    <meta key="PageID" name="PageID" content={data?.wpPost?.databaseId} />
    <title id="page-title">
      {data?.wpPost?.seoTitle ? data.wpPost.seoTitle : data.wpPost.title}
    </title>
    <meta name="description" content={data?.wpPost?.seoDesc} />
    {data?.wpPost?.seoSchema && (
      <script type="application/ld+json">
        {makeProdUrl(data.wpPost.seoSchema)}
      </script>
    )}
  </>
)

// configure page, for example set defer to use DSG
// export async function config() {
//   // Optionally use GraphQL here
//   return ({ params }) => {
//     return {
//       defer: true,
//     }
//   }
// }

export const query = graphql`
  query PostContent($uri: String!) {
    wpPost(uri: { eq: $uri }) {
      id
      date
      title
      seoTitle
      seoDesc
      seoSchema
      content
      uri
      databaseId
      topParentId
      breadcrumbs
      primaryCategorySlug
      relatedPosts {
        title
        uri
        excerpt
        featuredImageSrc
        featuredImageAlt
        primaryCategorySlug
      }
    }
    allWpMenuItem(filter: { locations: { in: LOCAL_MENU } }) {
      ...LocalMenuItemFields
    }
  }
`
